import { useEffect, useId, useRef, useState } from "react";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { IconButton } from "../IconButton";
import s from "./SearchField.module.scss";

export type SearchFieldProps = {
  /** Visually hidden label (required: the placeholder is not a label). */
  label: string;
  value: string;
  /** Fires debounced (and at once on clear). */
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  clearLabel: string;
  debounceMs?: number;
  /** The most characters the field takes (e.g. the server's limit), so the user sees where the words stop. */
  maxLength?: number;
};

export const SearchField = ({ label, value, onChange, placeholder, onClear, clearLabel, debounceMs = 300, maxLength }: SearchFieldProps) => {
  const id = useId();
  const [text, setText] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Outside change (e.g. "Clear search" elsewhere): follow it, unless it is just our own value coming back.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    clearTimeout(timer.current);
    lastEmitted.current = value;
    setText(value);
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const emit = (v: string) => { lastEmitted.current = v; onChangeRef.current(v); };

  const type = (v: string) => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => emit(v), debounceMs);
  };

  const clear = () => {
    clearTimeout(timer.current);
    setText("");
    emit("");
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div className={s.wrap}>
      <label className={VISUALLY_HIDDEN} htmlFor={id}>{label}</label>
      <input
        id={id} ref={inputRef} type="search" className={s.input} value={text} placeholder={placeholder} autoComplete="off" enterKeyHint="search" maxLength={maxLength}
        onChange={(e) => type(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape" && text) { e.preventDefault(); e.stopPropagation(); clear(); } }}
      />
      <SearchRoundedIcon aria-hidden className={s.icon} />
      {text && <IconButton className={s.tap} label={clearLabel} icon={<CloseRoundedIcon />} onClick={clear} />}
    </div>
  );
};
